// src/components/GlobalBlurredCollage.js
import React, { useEffect, useRef, useState } from 'react';
import { http } from '../lib/axios';

const pick = (arr, n) => arr.slice().sort(() => 0.5 - Math.random()).slice(0, n);
const COLLAGE_SESSION_KEY = 'global_blurred_collage_bg_v1';

let collageDataUrlCache = null;
let collageBuildPromise = null;

async function fetchArtworkUrls(path, headers) {
  try {
    // Axios: await + .data, no .ok/.json()
    const { data } = await http.get(path, headers ? { headers } : undefined);
    // Support both array and {results: [...]}
    const list = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
    const urls = [];
    for (const a of list) {
      if (Array.isArray(a.images)) {
        for (const img of a.images) {
          const src = img?.url || img?.image || img?.image_url;
          if (src) urls.push(src);
        }
      }
      if ((!Array.isArray(a.images) || a.images.length === 0) && a?.primary_image_url) {
        urls.push(a.primary_image_url);
      }
    }
    return urls;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('fetchArtworkUrls failed:', e?.message || e);
    return null;
  }
}

// Helper: load an image into an <img>, supporting auth-only URLs via blob
//async function loadImage(src, token) {
//  // If there is a token and the image is same-origin (or requires auth),
//  // fetch as blob with Authorization and convert to object URL
//  const needsAuth = !!token && /^https?:\/\/|^\//.test(src); // crude but works for same-origin URLs
//  if (needsAuth) {
//    try {
//      const resp = await fetch(src, {
//        credentials: 'include', // if your media also uses session cookies
//        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
//      });
//      if (!resp.ok) throw new Error(`image fetch ${resp.status}`);
//      const blob = await resp.blob();
//      const objectUrl = URL.createObjectURL(blob);
//      return await new Promise((resolve) => {
//        const im = new Image();
//        im.onload = () => {
//          URL.revokeObjectURL(objectUrl);
//          resolve(im);
//        };
//        im.onerror = () => resolve(null);
//        im.src = objectUrl;
//      });
//    } catch {
//      return null;
//    }
//  }
//
//  // Public image path – can load directly
//  return await new Promise((resolve) => {
//    const im = new Image();
//    im.crossOrigin = 'anonymous';
//    im.onload = () => resolve(im);
//    im.onerror = () => resolve(null);
//    im.src = src;
//  });
//}

// Normalize any absolute media URL to a relative one so CRA proxy handles it.
// - "https://192.168.1.201:8000/media/…/file.jpg" -> "/media/…/file.jpg"
// - "http://localhost:8000/media/…/file.jpg"     -> "/media/…/file.jpg"
// - "media/…/file.jpg"                           -> "/media/…/file.jpg"
// - already relative "/media/…/file.jpg"         -> "/media/…/file.jpg"
// Keep data/blob URLs unchanged.
function toRelativeMedia(url) {
  if (!url) return url;
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;

  // Already /media/… ?
  if (url.startsWith('/media/')) return url;

  // Absolute URL? Strip scheme+host.
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url.replace(/^https?:\/\/[^/]+/, '');
  }

  // Bare "media/…" or "./media/…"
  const trimmed = url.replace(/^\.?\/*/, ''); // remove leading "./" or "/"
  return trimmed.startsWith('media/') ? `/${trimmed}` : `/media/${trimmed}`;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();

    // With relative URLs via CRA proxy, the image is same-origin.
    // Do NOT set crossOrigin unless you intentionally need canvas readback from cross-origin.
    // If you ever flip back to absolute URLs with CORS headers, you'd use:
    // img.crossOrigin = 'anonymous';

    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);

    img.src = toRelativeMedia(src);
  });
}

function readSessionCollage() {
  try {
    return sessionStorage.getItem(COLLAGE_SESSION_KEY);
  } catch (_err) {
    return null;
  }
}

function writeSessionCollage(value) {
  try {
    sessionStorage.setItem(COLLAGE_SESSION_KEY, value);
  } catch (_err) {
    // Ignore session storage failures.
  }
}

async function buildCollageDataUrl() {
  // 1) Try authenticated-like nearby feed first.
  let urls = await fetchArtworkUrls(
    '/artworks/nearby_artists/?limit=50&distance=50&lat=34.0522&lng=-118.2437&view=card',
    undefined
  );

  // 2) Public fallback.
  if (!urls || urls.length === 0) {
    urls = await fetchArtworkUrls('/artworks/?limit=50&view=card');
  }

  if (!urls || urls.length === 0) {
    return null;
  }

  const picked = pick(urls, Math.min(12, urls.length));
  const cw = 1920;
  const ch = 1080;
  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d');

  const grad = ctx.createLinearGradient(0, 0, 0, ch);
  grad.addColorStop(0, '#101418');
  grad.addColorStop(1, '#0c0f12');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, cw, ch);

  const cols = 4;
  const rows = 3;
  const cellW = Math.ceil(cw / cols);
  const cellH = Math.ceil(ch / rows);

  const imgs = await Promise.all(picked.map((src) => loadImage(src).catch(() => null)));

  let i = 0;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const im = imgs[i % imgs.length];
      i += 1;
      if (!im) continue;
      const sw = im.naturalWidth || im.width;
      const sh = im.naturalHeight || im.height;
      const scale = Math.max(cellW / sw, cellH / sh);
      const dw = sw * scale;
      const dh = sh * scale;
      const dx = c * cellW + (cellW - dw) / 2;
      const dy = r * cellH + (cellH - dh) / 2;
      ctx.drawImage(im, dx, dy, dw, dh);
    }
  }

  return canvas.toDataURL('image/jpeg', 0.8);
}

async function getOrBuildCollageDataUrl() {
  if (collageDataUrlCache) return collageDataUrlCache;

  const sessionValue = readSessionCollage();
  if (sessionValue) {
    collageDataUrlCache = sessionValue;
    return sessionValue;
  }

  if (!collageBuildPromise) {
    collageBuildPromise = buildCollageDataUrl()
      .then((dataUrl) => {
        if (dataUrl) {
          collageDataUrlCache = dataUrl;
          writeSessionCollage(dataUrl);
        }
        return dataUrl;
      })
      .finally(() => {
        collageBuildPromise = null;
      });
  }

  return collageBuildPromise;
}

export default function GlobalBlurredCollage() {
  const [bgUrl, setBgUrl] = useState(() => collageDataUrlCache || readSessionCollage());
  const containerRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    async function go() {
      const nextBgUrl = await getOrBuildCollageDataUrl();
      if (!mounted) return;
      setBgUrl(nextBgUrl);
    }

    go();
    return () => { mounted = false; };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{ position: 'fixed', inset: 0, zIndex: -1, overflow: 'hidden' }}
    >
      {/* image layer with existing blur level */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          //backgroundImage: 'linear-gradient(180deg, #101418 0%, #0c0f12 100%)',
          backgroundImage: bgUrl
            ? `url(${bgUrl})`
            : 'linear-gradient(180deg, #101418 0%, #0c0f12 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          //filter: bgUrl ? 'blur(7px) grayscale(1) contrast(1.1)' : 'none',
          filter: bgUrl ? 'blur(7px)' : 'none',
          transform: bgUrl ? 'scale(1.04)' : 'none',
          transition: 'opacity 300ms ease',
          opacity: 1,
        }}
      />
      {/* LIGHTEN overlay applied on ALL pages */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(255,255,255,0.25)',
        }}
      />
    </div>
  );
}
